/* eslint no-param-reassign: 0 */
// eslint-disable-next-line
import { h, Component } from 'preact';
import classNames from 'classnames';

import hasTouchSupport from '../../../utils/has-touch-support';
import onPassiveScroll from './passive-on-scroll';
import BackToTopButton from '../back-to-top-button';
import ChatButton from '../chat-button';
import JoButton from '../jo-button';
import CopyLinkButton from '../copy-link-button';

import styles from './index.scss';
import Placeholder from './placeholder';

import Sticky from './sticky';

/**
 * window.APC_ENABLE_JO
 */

// Usage example

function onAnimationEndOnce(el, timeOutMs, callback) {
    let t;

    const handleAnimationEnd = () => {
        clearTimeout(t);
        el.removeEventListener('animationend', handleAnimationEnd);
        callback();
    };

    t = setTimeout(() => {
        handleAnimationEnd();
    }, timeOutMs);

    el.addEventListener('animationend', handleAnimationEnd, {
        once: true,
    });
}

/**
 * Condition function for sticky element. Triggers "show" on sticky
 * element if returned true and "hide" otherwise.
 * @param {Element} origEl original element which position is watched in viewport
 * @param {Element} [clonedEl] cloned element, optional
 */
const CONDITION_FUNC_STICK_IF_NOT_IN_VIEWPORT = origEl => {
    const elBRect = origEl.getBoundingClientRect();
    const elOutOfViewportTop = elBRect.bottom <= 64;

    const elOutOfViewportBottom =
        elBRect.bottom > (window.innerHeight || document.documentElement.clientHeight);

    return elOutOfViewportTop || elOutOfViewportBottom;
};

class ReadyCallbacks {
    constructor(callbacksArr = []) {
        callbacksArr.forEach(cb => cb());
    }

    push(callback) {
        callback();
    }
}

// window.APC_USC_READY = window.APC_USC_READY || [];
// window.APC_USC_READY.push(function () {
//     // USC is ready now
// });

class App extends Component {
    constructor(props) {
        super(props);

        window.APC_USC = {
            description: 'Universal Sticky Container',
            add: this.add.bind(this),
            remove: this.remove.bind(this),
            hide: this.hide.bind(this),
            show: this.show.bind(this),
            hideAll: this._hideAll.bind(this),
            makeSticky: this.makeSticky.bind(this),
            unstickElement: this.unstickElement.bind(this),
            CONDITION_FUNC_STICK_IF_NOT_IN_VIEWPORT,
        };

        this.state = {
            placeholders: [],
            enableJo: !!window.APC_ENABLE_JO,
            canRenderJo: false, // changes to true when JO_SHOW_TIMEOUT is out
            hasTouchSupport: hasTouchSupport(),
            isBannerShow: false,
        };

        Object.defineProperty(window, 'APC_ENABLE_JO', {
            get: () => this.state.enableJo,
            set: val => {
                this.setState({
                    enableJo: !!val,
                });
            },
        });

        this.JO_SHOW_TIMEOUT =
            typeof window.JO_SHOW_TIMEOUT !== 'undefined' ? window.JO_SHOW_TIMEOUT : 2000;

        Object.defineProperty(window, 'JO_SHOW_TIMEOUT', {
            get: () => this.JO_SHOW_TIMEOUT,
            set: val => {
                this.JO_SHOW_TIMEOUT = val;
            },
        });

        // array of elements which are made sticky ( by cloning )
        this.stickyElements = new Map();

        this.elPhwrapper = null;

        window.APC_USC_READY = new ReadyCallbacks(window.APC_USC_READY);
    }

    _hideAll(opts = {}) {
        const except = opts.except || [];
        const hasVisible = this.state.placeholders.find(ph => !ph.fadeOut);

        return new Promise(resolve => {
            if (!hasVisible) {
                resolve();
                return;
            }
            this.setState(
                ({ placeholders }) => ({
                    placeholders: placeholders.map(phItem => {
                        if (!except.includes(phItem)) {
                            phItem.fadeOut = true; // eslint-disable-line
                        }
                        return phItem;
                    }),
                }),
                () => {
                    onAnimationEndOnce(this.elPhwrapper, 600, () => {
                        this.setState(
                            ({ placeholders }) => ({
                                placeholders: placeholders.map(ph => {
                                    // hiding
                                    if (ph.fadeOut) {
                                        ph.hide = true; // eslint-disable-line
                                    }
                                    return ph;
                                }),
                            }),
                            resolve,
                        );
                    });
                },
            );
        });
    }

    _hideOne(ph) {
        return new Promise(resolve => {
            this.setState(
                ({ placeholders }) => ({
                    placeholders: placeholders.map(phItem => {
                        if (phItem === ph) {
                            phItem.fadeOut = true; // eslint-disable-line
                            phItem.hide = false; // eslint-disable-line
                        }
                        return phItem;
                    }),
                }),
                () => {
                    onAnimationEndOnce(this.elPhwrapper, 600, () => {
                        this.setState(
                            ({ placeholders }) => ({
                                placeholders: placeholders.map(pholder => {
                                    // hiding
                                    if (pholder.fadeOut) {
                                        pholder.hide = true; // eslint-disable-line
                                    }
                                    return pholder;
                                }),
                            }),
                            resolve,
                        );
                    });
                },
            );
        });
    }

    /**
     * Returns placeholder or searches for it by element
     */
    _getPlaceholder = (el, phItem = null) => {
        if (phItem) {
            return phItem;
        }

        return this.state.placeholders.find(ph => ph.el === el);
    };

    /**
     * Returns previous placeholder relative to passed one
     */
    _getPrevPlaceholder = phItem => {
        // const phIdx = this.state.placeholders.findIndex(ph => ph === phItem);
        // if (phIdx === -1) return null;
        // const prevPhIdx = phIdx - 1;

        // return prevPhIdx < 0 ? null : this.state.placeholders[prevPhIdx];

        const reversedPhArray = [...this.state.placeholders].reverse();
        const phIdx = reversedPhArray.findIndex(ph => ph === phItem);

        if (phIdx === -1) return null;

        const [prevPlaceholder] = reversedPhArray.filter((ph, i) => {
            if (i > phIdx && !ph.el.dataset.isHidden) return ph;

            return null;
        });

        return prevPlaceholder;
    };

    hide = (el, phItem = null) => {
        const togglePh = this._getPlaceholder(el, phItem);
        const prevPh = this._getPrevPlaceholder(togglePh);

        if (togglePh.hide) {
            return Promise.resolve(togglePh);
        }

        return this._hideOne(togglePh).then(() => {
            this.setState(({ placeholders }) => ({
                placeholders: placeholders.map(ph => {
                    // fading out element
                    if (ph === prevPh) {
                        ph.fadeOut = false; // eslint-disable-line
                        ph.hide = false;
                    }
                    return ph;
                }),
            }));
            return togglePh;
        });
    };

    show = (el, phItem = null) => {
        const togglePh = this._getPlaceholder(el, phItem);

        // early exit if visible
        if (!togglePh.fadeOut && !togglePh.hide) {
            return;
        }

        this._hideAll({ except: [togglePh] }).then(() => {
            this.setState(({ placeholders }) => ({
                placeholders: placeholders.map(ph => {
                    // fading out element
                    if (ph === togglePh) {
                        ph.fadeOut = false; // eslint-disable-line
                        ph.hide = false;
                    }
                    return ph;
                }),
            }));
        });
    };

    _addPlaceholder(ph) {
        // eslint-disable-next-line
        this.setState(({ placeholders }) => ({
            placeholders: [...placeholders, ph],
        }));
    }

    add(el, opts = { hidden: false }) {
        const ph = {
            id: `ph-${Date.now()}`,
            el,
            fadeOut: true,
            hide: true,
        };

        // eslint-disable-next-line
        this.setState(
            ({ placeholders }) => ({
                placeholders: [...placeholders, ph],
            }),
            () => {
                if (!opts.hidden) {
                    this.show(null, ph);
                }
            },
        );
    }

    remove(el) {
        return this.hide(el).then(removePh => {
            // remove from state
            this.setState(({ placeholders }) => ({
                placeholders: placeholders.filter(phItem => phItem !== removePh),
            }));
        });
    }
    isCookieBanner = () => {
        const isBannerShow = !this.cookieBanner.classList.contains('hide');
        this.setState({
            isBannerShow,
        });
    };
    watchBanner = () => {
        const config = {
            attributes: true,
        };
        const callback = mutationList => {
            // eslint-disable-next-line no-restricted-syntax
            for (const mutation of mutationList) {
                if (mutation.target.classList.contains('hide')) {
                    this.isCookieBanner();
                }
            }
        };
        this.observer = new MutationObserver(callback);
        this.observer.observe(this.cookieBanner, config);
    };

    componentDidMount() {
        this.calcPositions();
        this.scrollUnsubscribe = onPassiveScroll(this.calcPositions);
        window.addEventListener('resize', this.calcPositions);
        this.cookieBanner = document.querySelector(
            "section[class^='sdl-message-cookie-notification']",
        );
        if (this.cookieBanner) {
            this.isCookieBanner();
            this.watchBanner();
        }
    }

    componentWillUnmount() {
        this.scrollUnsubscribe();
        window.removeEventListener('resize', this.calcPositions);
        this.observer.disconnect();
    }

    calcPositions = () => {
        this.stickyElements.forEach(stickyObj => stickyObj.checkCondition());
    };

    makeSticky(element, conditionFunc, opts) {
        let stickyObj = this.stickyElements.get(element);
        if (stickyObj) {
            return stickyObj;
        }

        if (!conditionFunc) {
            conditionFunc = CONDITION_FUNC_STICK_IF_NOT_IN_VIEWPORT;
        }

        stickyObj = new Sticky(element, conditionFunc, opts);

        this.stickyElements.set(element, stickyObj);

        return stickyObj;
    }

    unstickElement(element) {
        const stickyObj = this.stickyElements.get(element);
        if (stickyObj) {
            stickyObj.destroy();
            this.stickyElements.delete(element);
            return true;
        }

        return false;
    }

    renderJoButton() {
        if (this.state.canRenderJo) {
            return <JoButton isBannerShow={this.state.isBannerShow} />;
        }

        if (this.tiShowJoButton) {
            return null;
        }

        this.tiShowJoButton = setTimeout(() => {
            this.setState(
                {
                    canRenderJo: true,
                },
                () => {
                    this.tiShowJoButton = null;
                },
            );
        }, this.JO_SHOW_TIMEOUT);

        return null;
    }

    render() {
        const USCClasses = classNames({
            [styles.universalStickyContainer]: true,
            'usc-no-touch-support': !this.state.hasTouchSupport,
        });

        return (
            <div className={USCClasses}>
                <div className={styles.stickyButtonsWrapper}>
                    <div className={styles.stickyButtons}>
                        <CopyLinkButton />
                        <BackToTopButton isBannerShow={this.state.isBannerShow} />

                        {this.state.enableJo ? (
                            this.renderJoButton()
                        ) : (
                            <ChatButton isBannerShow={this.state.isBannerShow} />
                        )}
                    </div>
                </div>
                <div
                    ref={el => {
                        this.elPhwrapper = el;
                    }}
                    className={styles.stickyElementsWrapper}
                >
                    {this.state.placeholders.map(ph => (
                        <Placeholder
                            el={ph.el}
                            phId={ph.id}
                            key={ph.id}
                            fadeOut={ph.fadeOut}
                            hide={ph.hide}
                        />
                    ))}
                </div>
            </div>
        );
    }
}

export default App;
